import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { availableLocales, getLocale } from '../../i18n/locales';
import { LANG_STORAGE_KEY } from '../../i18n';
import { isFeatureEnabled } from '../../utils/featureFlags';
import './LanguageSwitcher.css';

/**
 * Compact locale picker: flag + short code, with the endonym in the menu.
 *
 * A trigger-plus-menu rather than a row of chips because the target set is ten
 * locales — a row would dominate the header once Tier 2 and 3 land.
 *
 * The short code, not the flag, is what identifies the language: some locales
 * carry no flag on purpose (see PLANNED in i18n/locales.js), and a flag alone
 * is ambiguous for any language spoken in more than one country.
 */
const LanguageSwitcher = ({ className = '' }) => {
  const { t, i18n } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      // The menu is portaled out of the wrapper, so it counts as inside too.
      const inside = (ref.current && ref.current.contains(e.target))
        || (menuRef.current && menuRef.current.contains(e.target));
      if (!inside) setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Clamp the panel to the viewport. End-aligning a wide menu to a trigger
  // near the left edge previously pushed its flag/code columns off-screen.
  useLayoutEffect(() => {
    if (!open) return undefined;

    const placeMenu = () => {
      const trigger = triggerRef.current;
      const menu = menuRef.current;
      if (!trigger || !menu) return;

      const gutter = 8;
      const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const width = Math.min(220, Math.max(0, viewportWidth - gutter * 2));
      const triggerRect = trigger.getBoundingClientRect();
      const preferredLeft = triggerRect.right - width;
      const left = Math.min(
        Math.max(gutter, preferredLeft),
        Math.max(gutter, viewportWidth - width - gutter),
      );
      const top = triggerRect.bottom + 6;

      setMenuStyle({
        left: `${Math.round(left)}px`,
        top: `${Math.round(top)}px`,
        width: `${Math.round(width)}px`,
        maxHeight: `${Math.max(160, Math.floor(viewportHeight - top - gutter))}px`,
      });
    };

    placeMenu();
    window.addEventListener('resize', placeMenu);
    window.addEventListener('scroll', placeMenu, true);
    return () => {
      window.removeEventListener('resize', placeMenu);
      window.removeEventListener('scroll', placeMenu, true);
    };
  }, [open]);

  if (!isFeatureEnabled('I18N')) return null;

  const locales = availableLocales();
  if (locales.length < 2) return null;

  const active = getLocale(i18n.language);

  const choose = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
    try {
      localStorage.setItem(LANG_STORAGE_KEY, code);
    } catch {
      // Private mode / storage disabled — the choice just won't persist.
    }
  };

  return (
    <div className={`lang-switcher ${className}`.trim()} ref={ref}>
      <button
        type="button"
        ref={triggerRef}
        className="lang-switcher-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('language.changeLanguage')}
        title={active.endonym}
      >
        {active.flag && <span className="lang-flag" aria-hidden="true">{active.flag}</span>}
        <span className="lang-code">{active.short}</span>
      </button>

      {/* Portaled to <body>: the header has `backdrop-filter`, which makes it
          the containing block of anything position:fixed inside it, so the
          viewport coordinates computed above landed the menu one sidebar-width
          to the right — under the Lens pill. */}
      {open && createPortal(
        <ul
          ref={menuRef}
          className={`lang-switcher-menu ${menuStyle ? 'is-positioned' : ''}`}
          style={menuStyle || undefined}
          role="listbox"
          aria-label={t('language.switcherLabel')}
        >
          {locales.map((l) => (
            <li key={l.code}>
              <button
                type="button"
                role="option"
                aria-selected={l.code === active.code}
                className={`lang-switcher-item ${l.code === active.code ? 'is-active' : ''}`}
                onClick={() => choose(l.code)}
                // Keep the flag/code/name grid physical; only the endonym below
                // uses its native writing direction.
                dir="ltr"
              >
                <span className="lang-flag" aria-hidden="true">{l.flag || ''}</span>
                <span className="lang-code">{l.short}</span>
                <span className="lang-endonym" dir={l.dir} lang={l.code}>{l.endonym}</span>
              </button>
            </li>
          ))}
        </ul>,
        document.body,
      )}
    </div>
  );
};

export default LanguageSwitcher;
